import { formatStringToDate } from "go-core/components/Formatters/FormatDate";
import { parseRangeToDefaultString } from "go-list/utils/daterangeutils";
import { ReportComboDateUtils } from "./comboSimpleDateUtils";
import {
	ColumnConfigValueReportComboApi,
	ValidationReportComboException,
	VariableCalculateConfigReportComboApi,
	VariableConfigReportComboApi,
} from "./types";

function getMonthsRelativeToDate(startDate: Date, count: number): number[] {
	const result: number[] = [];
	const direction = Math.sign(count);
	const current = new Date(startDate.getFullYear(), startDate.getMonth(), 1);

	for (let i = 0; i < Math.abs(count); i++) {
		result.push(current.getMonth() + 1); // 1-based month
		current.setMonth(current.getMonth() + direction);
	}

	return result;
}

export const getQuarter = (date: Date, quarters: number) => {
	const startDate = new Date(date);
	startDate.setMonth(startDate.getMonth() - quarters * 3);

	const startMonth = Math.floor(startDate.getMonth() / 3) * 3;
	startDate.setMonth(startMonth, 1);
	startDate.setHours(0, 0, 0, 0);

	return startDate;
};
export const getPredefineKeys = () => {
	return [
		"last_{x}_months",
		"previous_{x}_months",
		"previous_{x}_quarter",
		"previous_{x}_years",
		"previous_{x}_weeks",
		"this_month",
	];
};
const parseCalculateMethod = (configCalculate: VariableCalculateConfigReportComboApi, value: string) => {
	const method = configCalculate.method;
	let configValue = configCalculate.value;
	const valueSplit = value.split("¥");
	switch (method) {
		case "previous_weeks":
			if (configValue === undefined) configValue = 1;
			if (valueSplit.length > 1) {
				const dateForWeek = formatStringToDate(valueSplit[0]);
				const weekInfo = ReportComboDateUtils.getNumberOfWeek(dateForWeek, configValue);
				const dateRange = ReportComboDateUtils.getISOWeekRange(weekInfo.year, weekInfo.week);
				return parseRangeToDefaultString({
					startDate: dateRange.from,
					endDate: dateRange.to,
				});
			}
			break;
		case "previous_week_by_years":
			if (configValue === undefined) configValue = 1;
			if (valueSplit.length > 1) {
				const dateForWeek = formatStringToDate(valueSplit[0]);
				const weekInfo = ReportComboDateUtils.getISOWeekInfo(dateForWeek);
				const dateRange = ReportComboDateUtils.getISOWeekRange(weekInfo.year - configValue, weekInfo.week);
				return parseRangeToDefaultString({
					startDate: dateRange.from,
					endDate: dateRange.to,
				});
			}
			break;
		case "previous_year_to_date":
			if (configValue === undefined) configValue = 1;
			if (valueSplit.length > 1) {
				const dateForWeek = formatStringToDate(valueSplit[1]);
				const dateRange = ReportComboDateUtils.getDateRangeForPreviousYear(dateForWeek, configValue);
				return parseRangeToDefaultString({
					startDate: dateRange.from,
					endDate: dateRange.to,
				});
			}
			break;
		case "previous_month_to_date":
			if (configValue === undefined) configValue = 1;
			if (valueSplit.length > 1) {
				const dateForWeek = formatStringToDate(valueSplit[1]);
				const dateRange = ReportComboDateUtils.getDateRangeForPreviousMonth(dateForWeek, configValue);
				return parseRangeToDefaultString({
					startDate: dateRange.from,
					endDate: dateRange.to,
				});
			}
			break;
		case "list_previous_months":
			if (configValue === undefined) configValue = 1;
			if (valueSplit.length > 1) {
				const to = formatStringToDate(valueSplit[1]);
				const months = getMonthsRelativeToDate(to, configValue * -1);
				return months.join(",");
			}
			break;
	}
	return undefined;
};

const getContentInsideBraces = (value: any): string | undefined => {
	if (typeof value === "string" && value.startsWith("{") && value.endsWith("}")) {
		return value.slice(1, -1);
	}
	return undefined;
};

const predefineToValues = (predefinedRange: string, date: Date): string | undefined => {
	let startDate;
	let endDate;

	switch (predefinedRange) {
		case "this_month":
			startDate = new Date(date.getFullYear(), date.getMonth(), 1, 0, 0, 0);
			endDate = new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59);
			break;
		default: {
			let match = predefinedRange.match(/^last_(\d+)-(\d+)_months$/);
			if (match) {
				const monthsAgoEnd = parseInt(match[1], 10); // Older range (e.g., 6 months ago)
				const monthsAgoStart = parseInt(match[2], 10); // More recent range (e.g., 3 months ago)
				startDate = new Date(date.getFullYear(), date.getMonth() - monthsAgoStart, date.getDate(), 0, 0, 0);
				endDate = new Date(date.getFullYear(), date.getMonth() - monthsAgoEnd, date.getDate(), 23, 59, 59);
				break;
			}
			match = predefinedRange.match(/^last_(\d+)_months$/);
			if (match) {
				const value = parseInt(match[1], 10);
				startDate = new Date(date.getFullYear(), date.getMonth() - value, date.getDate(), 0, 0, 0);
				endDate = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59);
				break;
			}
			//months
			match = predefinedRange.match(/^previous_(\d+)-(\d+)_months$/);
			if (match) {
				const monthsAgoEnd = parseInt(match[1], 10); // Older range (e.g., 6 months ago)
				const monthsAgoStart = parseInt(match[2], 10); // More recent range (e.g., 3 months ago)
				startDate = new Date(date.getFullYear(), date.getMonth() - monthsAgoStart, 1, 0, 0, 0);
				endDate = new Date(date.getFullYear(), date.getMonth() - monthsAgoEnd + 1, 0, 23, 59, 59);
				break;
			}
			match = predefinedRange.match(/^previous_(\d+)_months$/);
			if (match) {
				const value = parseInt(match[1], 10);
				startDate = new Date(date.getFullYear(), date.getMonth() - value, 1, 0, 0, 0);
				endDate = new Date(date.getFullYear(), date.getMonth(), 0, 23, 59, 59);
				break;
			}

			//quarters
			match = predefinedRange.match(/^previous_(\d+)_quarters$/);
			if (match) {
				const value = parseInt(match[1], 10);
				startDate = getQuarter(date, value);
				endDate = new Date(startDate.getFullYear(), startDate.getMonth() + 3, 0, 23, 59, 59);
				break;
			}

			//weeks
			match = predefinedRange.match(/^previous_(\d+)_weeks_previous_(\d+)_years/);
			if (match) {
				const value = parseInt(match[1], 10);
				const numberOfYears = parseInt(match[2], 10);
				const weekInfo = ReportComboDateUtils.getNumberOfWeek(date, value);
				const lastYearWeekInfo = ReportComboDateUtils.getISOWeekRange(
					weekInfo.year - numberOfYears,
					weekInfo.week
				);
				startDate = lastYearWeekInfo.from;
				endDate = lastYearWeekInfo.to;
				break;
			}
			match = predefinedRange.match(/^previous_(\d+)_weeks_year_this/);
			if (match) {
				const value = parseInt(match[1], 10);
				const weekInfo = ReportComboDateUtils.getNumberOfWeek(date, value);
				const lastYearWeekInfo = ReportComboDateUtils.getISOWeekRange(weekInfo.year, weekInfo.week);
				const yearRange = ReportComboDateUtils.getDateRangeForPreviousYear(lastYearWeekInfo.to, 0);
				startDate = yearRange.from;
				endDate = yearRange.to;
				break;
			}
			match = predefinedRange.match(/^previous_(\d+)_weeks_year_previous_(\d+)_years/);
			if (match) {
				const value = parseInt(match[1], 10);
				const numberOfYears = parseInt(match[2], 10);
				const weekInfo = ReportComboDateUtils.getNumberOfWeek(date, value);
				const lastYearWeekInfo = ReportComboDateUtils.getISOWeekRange(weekInfo.year, weekInfo.week);
				const yearRange = ReportComboDateUtils.getDateRangeForPreviousYear(lastYearWeekInfo.to, numberOfYears);
				startDate = yearRange.from;
				endDate = yearRange.to;
				break;
			}
			match = predefinedRange.match(/^previous_(\d+)_weeks/);
			if (match) {
				const value = parseInt(match[1], 10);
				const weekInfo = ReportComboDateUtils.getNumberOfWeek(date, value);
				const lastYearWeekInfo = ReportComboDateUtils.getISOWeekRange(weekInfo.year, weekInfo.week);
				startDate = lastYearWeekInfo.from;
				endDate = lastYearWeekInfo.to;
				break;
			}
			match = predefinedRange.match(/^previous_(\d+)-(\d+)_weeks/);
			if (match) {
				const value = parseInt(match[1], 10); // Older range (e.g., 6 months ago)
				const value2 = parseInt(match[2], 10); // More recent range (e.g., 3 months ago)
				const firstWeekInfo = ReportComboDateUtils.getNumberOfWeek(date, value);
				const secondWeekInfo = ReportComboDateUtils.getNumberOfWeek(date, value2);
				const firstDates = ReportComboDateUtils.getISOWeekRange(firstWeekInfo.year, firstWeekInfo.week);
				const secondDates = ReportComboDateUtils.getISOWeekRange(secondWeekInfo.year, secondWeekInfo.week);
				startDate = firstDates.from;
				endDate = secondDates.to;
				break;
			}
			//years
			match = predefinedRange.match(/^previous_(\d+)-(\d+)_years/);
			if (match) {
				const yearsAgoEnd = parseInt(match[1], 10); // Older range (e.g., 6 months ago)
				const yearsAgoStart = parseInt(match[2], 10); // More recent range (e.g., 3 months ago)
				startDate = new Date(date.getFullYear() - yearsAgoStart, 0, 1, 0, 0, 0);
				endDate = new Date(date.getFullYear() - yearsAgoEnd, 12, 0, 23, 59, 59);
				break;
			}
			match = predefinedRange.match(/^previous_(\d+)_years$/);
			if (match) {
				const value = parseInt(match[1], 10);
				startDate = new Date(date.getFullYear() - value, 0, 1, 0, 0, 0);
				endDate = new Date(date.getFullYear(), 12, 0, 23, 59, 59);
				break;
			}
		}
	}
	if (!startDate) return undefined;

	return parseRangeToDefaultString({
		startDate,
		endDate,
	});
};

export class VariableReportComboUtils {
	static parsePredefinedToValues = (value: string, date: Date): string | undefined => {
		if (value) {
			const predefinedKeys = getPredefineKeys();
			for (const key of predefinedKeys) {
				const regex = new RegExp(key.replace("{x}", "\\d+(-\\d+)?"));
				if (regex.test(value)) {
					return predefineToValues(value, date);
				}
			}
		}
		return undefined;
	};

	static parseVariableForValue = (
		value: string,
		date: Date,
		variables?: VariableConfigReportComboApi[],
		variableValues?: Record<string, string>
	): string | undefined => {
		if (value) {
			const variableId = getContentInsideBraces(value);
			if (variableId) {
				const variable = variables?.filter((x) => x.id === variableId)[0];
				if (!variable) {
					return value;
				}
				if (variable.type === "DYNAMIC") {
					if (!variable.calculate)
						throw new ValidationReportComboException(
							`Variable calculate config not exist in configuration: ${variableId}`
						);
					const calculateVariableId = variable.calculate?.variable_id;
					if (!calculateVariableId) {
						throw new ValidationReportComboException(
							`Calculate variable value not exist: ${calculateVariableId}`
						);
					}
					let calculateVariableValue = variableValues ? variableValues[calculateVariableId] : undefined;
					if (!calculateVariableValue) {
						throw new ValidationReportComboException(
							`Calculate variable value not exist: ${calculateVariableId}`
						);
					}
					calculateVariableValue =
						VariableReportComboUtils.parsePredefinedToValues(calculateVariableValue, date) ??
						calculateVariableValue;
					return parseCalculateMethod(variable.calculate, calculateVariableValue);
				}

				const variableValue = variableValues ? variableValues[variableId] : undefined;
				if (!variableValue) {
					throw new ValidationReportComboException(`Variable value not exist: ${variableId}`);
				}
				return variableValue;
			}
		}
		return VariableReportComboUtils.parsePredefinedToValues(value, date);
	};
	static getDynamicValues = (
		dynamicValue: string,
		valueVariables: Record<string, string>,
		variables?: VariableConfigReportComboApi[]
	) => {
		const result: ColumnConfigValueReportComboApi[] = [];
		if (!variables) return result;
		const variable = variables.filter((x) => x.id === dynamicValue)[0];
		if (variable.type === "DYNAMIC" && variable.calculate) {
			const variableCalculate = variable.calculate;
			const valueVariable = valueVariables[variableCalculate?.variable_id];
			const valueVariableValue = VariableReportComboUtils.parsePredefinedToValues(valueVariable, new Date());
			if (!valueVariableValue)
				throw new ValidationReportComboException(`Dynamic value variable value not exist: ${dynamicValue}`);
			const stringValues = parseCalculateMethod(variableCalculate, valueVariableValue);
			return stringValues?.split(",").map((x) => {
				return {
					id: x.toString(),
				} as ColumnConfigValueReportComboApi;
			});
			// const valueSplit = valueVariableValue.split("¥");
			// const from = formatStringToDate(valueSplit[0]);
			// const to = formatStringToDate(valueSplit[1]);
			// if(!from || !to) throw new ValidationReportComboException(`Dynamic value from and to not exist: ${dynamicValue}`);
			// const current = new Date(from.getFullYear(), from.getMonth(), 1);
			//
			// while (current <= to) {
			//     result.push({
			//         id: (current.getMonth() + 1).toString()
			//     } as ColumnConfigValueReportComboApi); // getMonth() is 0-based
			//     current.setMonth(current.getMonth() + 1);
			// }
		}
		return result;
	};
}
